import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Rocket, Loader2, Radar, Wand2, FileText, Clapperboard, Film,
  CheckCircle2, XCircle, AlertTriangle, Clock, Play, RotateCcw, Ban,
  ExternalLink, ShieldCheck, Globe, BrainCircuit, Gauge, Cpu, Bot, User, Wrench,
} from "lucide-react";
import { logger } from "@/lib/logger";
import { useNavigate } from "react-router-dom";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";

const STATUS_UI = {
  waiting: { cls: "bg-slate-700/40 text-slate-300 border-slate-600/40", Icon: Clock, label: "Waiting" },
  researching: { cls: "bg-sky-500/10 text-sky-300 border-sky-500/30", Icon: Radar, label: "Researching" },
  "blueprint-ready": { cls: "bg-amber-500/10 text-amber-300 border-amber-500/30", Icon: AlertTriangle, label: "Blueprint — needs approval" },
  running: { cls: "bg-violet-500/10 text-violet-300 border-violet-500/30", Icon: Loader2, label: "Running", spin: true },
  "needs-review": { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", Icon: CheckCircle2, label: "Ready for review" },
  failed: { cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", Icon: XCircle, label: "Failed" },
  cancelled: { cls: "bg-slate-700/40 text-slate-400 border-slate-600/40", Icon: Ban, label: "Cancelled" },
  succeeded: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", Icon: CheckCircle2, label: "Succeeded" },
};

const STEP_ICON = { project: Film, script: FileText, storyboard: Clapperboard, render: Film };

function StatusPill({ status }) {
  const s = STATUS_UI[status] || STATUS_UI.waiting;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${s.cls}`} data-testid={`autopilot-status-${status}`}>
      <s.Icon className={`w-3 h-3 ${s.spin ? "animate-spin" : ""}`} /> {s.label}
    </span>
  );
}

function CreateForm({ channelId, onCreated }) {
  const [topic, setTopic] = useState("");
  const [extra, setExtra] = useState("");
  const [urls, setUrls] = useState("");
  const [styleId, setStyleId] = useState("");
  const [styles, setStyles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/styles").then((r) => setStyles(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!channelId) { toast.error("Select a channel first (top-left selector)."); return; }
    if (!topic.trim()) { toast.error("Topic is required."); return; }
    setBusy(true);
    try {
      const source_urls = urls.split("\n").map((u) => u.trim()).filter(Boolean);
      const { data } = await api.post("/autopilot/runs", {
        channel_id: channelId, topic: topic.trim(),
        extra_instructions: extra.trim(), source_urls, style_id: styleId || null,
      });
      toast.success("Autopilot run started — research is beginning.");
      setTopic(""); setExtra(""); setUrls("");
      onCreated(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid="autopilot-create-form">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center flex-none">
          <Rocket className="w-4 h-4 text-white" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-slate-100">Create from topic</h2>
          <p className="text-xs text-slate-500">Topic → research → Director blueprint → video. Stops at review.</p>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-slate-300 text-sm mb-1 block">Topic</Label>
          <Textarea className={`${inputCls} min-h-[70px]`} value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Neft qiymətlərinin dünya iqtisadiyyatına təsiri" data-testid="autopilot-topic" />
          <p className="text-[11px] text-slate-600 mt-1">Output language always follows the channel profile, whatever language you type here.</p>
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-1 block">Extra instructions <span className="text-slate-600">(optional)</span></Label>
          <Input className={inputCls} value={extra} onChange={(e) => setExtra(e.target.value)}
            placeholder="tone, angle, constraints…" data-testid="autopilot-extra" />
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-1 block">Source URLs <span className="text-slate-600">(optional, one per line)</span></Label>
          <Textarea className={`${inputCls} min-h-[52px] font-mono text-xs`} value={urls} onChange={(e) => setUrls(e.target.value)}
            placeholder={"https://…\nhttps://…"} data-testid="autopilot-urls" />
          <p className="text-[11px] text-slate-600 mt-1">Fetched &amp; labelled as reviewed evidence. Without URLs the brief is honestly labelled "model knowledge".</p>
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-1 block">Style <span className="text-slate-600">(optional)</span></Label>
          <Select value={styleId || "none"} onValueChange={(v) => setStyleId(v === "none" ? "" : v)}>
            <SelectTrigger className={inputCls} data-testid="autopilot-style"><SelectValue placeholder="No style" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
              <SelectItem value="none">No style</SelectItem>
              {styles.map((s) => <SelectItem key={s.id} value={s.id}>{s.data.name} (v{s.version})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={submit} disabled={busy} className="w-full bg-violet-600 hover:bg-violet-500 text-white" data-testid="autopilot-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1.5" /> Launch autopilot</>}
        </Button>
      </div>
    </div>
  );
}

function RunsList({ runs, selectedId, onSelect }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-3" data-testid="autopilot-runs-list">
      <p className="px-2 py-1 text-[10px] font-semibold tracking-widest uppercase text-slate-600">Recent runs</p>
      {runs.length === 0 ? (
        <p className="text-xs text-slate-600 px-2 py-3">No autopilot runs yet.</p>
      ) : (
        <div className="space-y-1 max-h-[320px] overflow-y-auto">
          {runs.map((r) => (
            <button key={r.id} onClick={() => onSelect(r.id)} data-testid={`autopilot-run-${r.id}`}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${selectedId === r.id ? "bg-violet-600/15" : "hover:bg-slate-800/50"}`}>
              <div className="text-sm text-slate-200 truncate">{r.topic}</div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <StatusPill status={r.status} />
                <span className="text-[10px] text-slate-600">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BlueprintReview({ run, onApprove, onRevise, busy }) {
  const bp = run.blueprint;
  const [revising, setRevising] = useState(false);
  const [instr, setInstr] = useState("");
  if (!bp) return null;
  return (
    <div className="bg-[#111827] border border-amber-500/30 rounded-xl p-5" data-testid="autopilot-blueprint-review">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-amber-300" />
        <h3 className="text-sm font-semibold text-slate-100">Production Blueprint — review before execution</h3>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 ml-auto">v{bp.version}</span>
      </div>
      <div className="space-y-3 text-sm">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Angle</p>
          <p className="text-slate-100" data-testid="blueprint-angle">{bp.angle || "—"}</p>
          {bp.rationale && <p className="text-xs text-slate-400 mt-1">{bp.rationale}</p>}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">{bp.format} · {bp.duration}s · {bp.aspect}</span>
          <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">{bp.scene_count} scenes</span>
          <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">captions: {bp.caption_approach}</span>
          <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">music: {bp.music_approach}</span>
        </div>
        {bp.narrative_structure && (
          <div><p className="text-[11px] uppercase tracking-wide text-slate-500">Structure</p><p className="text-slate-300 text-xs">{bp.narrative_structure}</p></div>
        )}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Scene plan</p>
          <div className="space-y-1.5" data-testid="blueprint-scenes">
            {(bp.scenes || []).map((s, i) => (
              <div key={`bp-scene-${i}-${s.tool_id || "x"}`} className="flex items-start gap-2 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2">
                <span className="text-[10px] text-violet-300 font-mono mt-0.5">#{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-slate-200 text-xs">{s.beat}</p>
                  <p className="text-slate-500 text-[11px]">{s.visual_approach} · <span className="font-mono">{s.tool_id}</span> · {s.narration_approach}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Tools selected (validated against registry)</p>
          <div className="flex flex-wrap gap-1.5" data-testid="blueprint-tools">
            {(bp.tool_ids || []).map((t) => (
              <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-violet-500/15 text-violet-300 font-mono">{t}</span>
            ))}
          </div>
        </div>
        {bp.total_estimate != null && (
          <p className="text-xs text-slate-400">Estimated total cost: <span className="text-slate-100">${bp.total_estimate}</span></p>
        )}
      </div>

      {!revising ? (
        <div className="flex items-center gap-2 mt-4">
          <Button onClick={onApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="blueprint-approve">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1.5" /> Approve &amp; execute</>}
          </Button>
          <Button onClick={() => setRevising(true)} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="blueprint-revise-open">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Request revision
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <Textarea className={`${inputCls} min-h-[60px]`} value={instr} onChange={(e) => setInstr(e.target.value)}
            placeholder="What should the Director change?" data-testid="blueprint-revise-input" />
          <div className="flex items-center gap-2">
            <Button onClick={() => { onRevise(instr); setRevising(false); setInstr(""); }} disabled={busy || !instr.trim()}
              className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="blueprint-revise-submit">Send revision</Button>
            <Button onClick={() => setRevising(false)} variant="ghost" className="text-slate-400 hover:bg-slate-800">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const VERDICT_UI = {
  ready: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", label: "Ready" },
  "ready-with-warnings": { cls: "bg-amber-500/10 text-amber-300 border-amber-500/30", label: "Ready with warnings" },
  "needs-work": { cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", label: "Needs work" },
};
const CHECK_UI = {
  pass: { cls: "text-emerald-400", Icon: CheckCircle2 },
  warn: { cls: "text-amber-300", Icon: AlertTriangle },
  fail: { cls: "text-rose-300", Icon: XCircle },
};
const PROV_UI = {
  automated: { label: "automated", Icon: Gauge, cls: "bg-sky-500/15 text-sky-300" },
  ai: { label: "AI evaluation", Icon: Bot, cls: "bg-violet-500/15 text-violet-300" },
  human: { label: "human", Icon: User, cls: "bg-slate-600/40 text-slate-300" },
};

function QualityReport({ report, unresolved, revisions, cycles }) {
  const v = VERDICT_UI[report.verdict] || VERDICT_UI["needs-work"];
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid="autopilot-quality">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Gauge className="w-4 h-4 text-violet-300" />
        <h3 className="text-sm font-semibold text-slate-100">Quality gate</h3>
        <span className={`text-xs px-2.5 py-1 rounded-full border ${v.cls}`} data-testid="quality-verdict">{v.label}</span>
        {cycles > 0 && <span className="text-[11px] text-slate-500" data-testid="quality-cycles">{cycles} revision cycle(s)</span>}
        <span className="ml-auto text-[11px] text-slate-600">{report.summary?.pass || 0} pass · {report.summary?.warn || 0} warn · {report.summary?.fail || 0} fail</span>
      </div>
      <div className="space-y-1.5" data-testid="quality-checks">
        {(report.checks || []).map((c, i) => {
          const ci = CHECK_UI[c.status] || CHECK_UI.warn;
          const pv = PROV_UI[c.provenance] || PROV_UI.automated;
          return (
            <div key={c.id} className="flex items-start gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-2" data-testid={`quality-check-${c.id}`}>
              <ci.Icon className={`w-4 h-4 mt-0.5 flex-none ${ci.cls}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-200">{c.label}</span>
                  <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${pv.cls}`}><pv.Icon className="w-2.5 h-2.5" /> {pv.label}</span>
                </div>
                {c.detail && <p className="text-[11px] text-slate-500 mt-0.5">{c.detail}</p>}
              </div>
            </div>
          );
        })}
      </div>
      {revisions?.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Revision log</p>
          <div className="space-y-1" data-testid="quality-revisions">
            {revisions.map((r, i) => (
              <div key={`rev-${r.cycle}`} className="text-[11px] text-slate-400"><span className="text-violet-300">Cycle {r.cycle}:</span> {r.rationale} <span className="text-slate-600">({(r.fixed || []).join(", ")})</span></div>
            ))}
          </div>
        </div>
      )}
      {unresolved?.length > 0 && (
        <div className="mt-3 bg-rose-500/5 border border-rose-500/20 rounded-lg p-3" data-testid="quality-unresolved">
          <p className="text-[11px] uppercase tracking-wide text-rose-300 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Unresolved — review tasks for a human</p>
          {unresolved.map((u, i) => (
            <div key={u.id || u.label} className="text-[11px] text-slate-300"><span className="text-rose-300">•</span> {u.label}: <span className="text-slate-500">{u.detail}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}

function ErrorDisplay({ error }) {
  if (!error) return null;
  return (
    <div className="mt-3 flex items-start gap-2 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="monitor-error">
      <AlertTriangle className="w-4 h-4 text-rose-300 mt-0.5 flex-none" />
      <p className="text-xs text-rose-200">{error}</p>
    </div>
  );
}

function StatusPanel({ run, busy, active, onCancel, onRetry, navigate }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-slate-100 truncate" data-testid="monitor-topic">{run.topic}</h2>
          <p className="text-xs text-slate-500 mt-1">{run.channel_name} · step: <span className="text-slate-300">{run.current_step}</span>{run.blueprint?.style_name ? <> · style: <span className="text-violet-300">{run.blueprint.style_name} v{run.blueprint.style_version}</span></> : null}</p>
        </div>
        <StatusPill status={run.status} />
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs">
        <span className="text-slate-400">Live cost: <span className="text-slate-100 font-medium" data-testid="monitor-cost">${(run.cost_so_far || 0).toFixed(4)}</span></span>
        <span className="text-slate-500">{(run.checkpoints || []).length} checkpoints</span>
      </div>
      <div className="flex items-center gap-2 mt-4">
        {active && <Button onClick={onCancel} disabled={busy} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="monitor-cancel"><Ban className="w-4 h-4 mr-1.5" /> Cancel</Button>}
        {run.status === "failed" && <Button onClick={onRetry} disabled={busy} size="sm" className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="monitor-retry"><RotateCcw className="w-4 h-4 mr-1.5" /> Retry from last step</Button>}
        {run.status === "needs-review" && run.project && (
          <Button onClick={() => navigate(`/projects/${run.project.id}?tab=review`)} size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="monitor-open-review">
            <ExternalLink className="w-4 h-4 mr-1.5" /> Open for review
          </Button>
        )}
      </div>
      <ErrorDisplay error={run.error} />
    </div>
  );
}

function stepState(isDone, isCurrent) {
  if (isDone) return { box: "bg-emerald-500/15 text-emerald-400", label: "text-slate-200" };
  if (isCurrent) return { box: "bg-violet-500/15 text-violet-300", label: "text-violet-200" };
  return { box: "bg-slate-800 text-slate-600", label: "text-slate-500" };
}

function ProgressTracker({ run, done, active, pipeline }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-slate-100 mb-3 flex items-center gap-2"><Clapperboard className="w-4 h-4 text-violet-300" /> Execution steps</h3>
      <div className="space-y-2" data-testid="autopilot-steps">
        {pipeline.map((name) => {
          const Icon = STEP_ICON[name] || Film;
          const isDone = done.has(name);
          const isCurrent = active && run.current_step === name;
          const cls = stepState(isDone, isCurrent);
          return (
            <div key={name} className="flex items-center gap-3" data-testid={`step-${name}`}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-none ${cls.box}`}>
                {isCurrent && !isDone ? <Loader2 className="w-4 h-4 animate-spin" /> : isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-sm capitalize ${cls.label}`}>{name}</span>
              {name === "render" && run.project?.renders?.length > 0 && (
                <span className="text-[11px] text-slate-500 ml-auto">render {run.project.renders[0].status}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Monitor({ runId }) {
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef();

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/autopilot/runs/${runId}`); setRun(data); }
    catch (e) { logger.error("autopilot run poll failed", e); /* transient poll error — keep last state */ }
  }, [runId]);

  useEffect(() => {
    setRun(null); load();
    timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, [runId, load]);

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const approve = () => act(async () => { await api.post(`/autopilot/runs/${runId}/approve-blueprint`); toast.success("Blueprint approved — executing."); });
  const revise = (instructions) => act(async () => { await api.post(`/autopilot/runs/${runId}/revise-blueprint`, { instructions }); toast.success("Revision requested."); });
  const cancel = () => act(async () => { await api.post(`/autopilot/runs/${runId}/cancel`); toast("Run cancelled."); });
  const retry = () => act(async () => { await api.post(`/autopilot/runs/${runId}/retry`); toast.success("Retrying from last checkpoint."); });

  if (!run) return <LoadingState label="Loading run…" />;

  const steps = run.steps || [];
  const done = new Set(steps.filter((s) => s.status === "done").map((s) => s.name));
  const pipeline = ["project", "script", "storyboard", "render"];
  const active = !["needs-review", "failed", "cancelled", "succeeded"].includes(run.status);

  return (
    <div className="space-y-4" data-testid="autopilot-monitor">
      <StatusPanel run={run} busy={busy} active={active} onCancel={cancel} onRetry={retry} navigate={navigate} />

      {run.status === "blueprint-ready" && <BlueprintReview run={run} onApprove={approve} onRevise={revise} busy={busy} />}

      {run.quality_report && <QualityReport report={run.quality_report} unresolved={run.unresolved_issues} revisions={run.revisions} cycles={run.revision_cycles} />}

      {run.research && (
        <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid="autopilot-research">
          <div className="flex items-center gap-2 mb-3">
            <Radar className="w-4 h-4 text-sky-300" />
            <h3 className="text-sm font-semibold text-slate-100">Research brief</h3>
            <span className={`ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${run.research.provenance?.includes("fetched") ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`} data-testid="research-provenance">
              {run.research.provenance?.includes("fetched") ? <Globe className="w-3 h-3" /> : <BrainCircuit className="w-3 h-3" />}
              {run.research.provenance}
            </span>
          </div>
          {run.research.context_summary && <p className="text-xs text-slate-300 mb-2">{run.research.context_summary}</p>}
          {run.research.topic_analysis && <p className="text-xs text-slate-400 mb-2">{run.research.topic_analysis}</p>}
          {(run.research.angles || []).length > 0 && (
            <div className="space-y-1 mb-2">
              {run.research.angles.map((a, i) => (
                <div key={`angle-${i}-${(a.angle || "").slice(0, 24)}`} className="text-xs text-slate-300"><span className="text-violet-300">•</span> <span className="text-slate-200">{a.angle}</span> — <span className="text-slate-500">{a.reason}</span></div>
              ))}
            </div>
          )}
          {run.research.caveat && <p className="text-[11px] text-amber-300/80 italic">Caveat: {run.research.caveat}</p>}
          {(run.research.sources || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {run.research.sources.map((s, i) => (
                <div key={s.url || `src-${i}`} className="text-[11px] flex items-center gap-1.5">
                  {s.reviewed ? <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-none" /> : <XCircle className="w-3 h-3 text-slate-500 flex-none" />}
                  <span className="text-slate-500 truncate">{s.url}</span>
                  <span className="text-slate-600">— {s.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ProgressTracker run={run} done={done} active={active} pipeline={pipeline} />

      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-100 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Checkpoints</h3>
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="autopilot-checkpoints">
          {(run.checkpoints || []).slice().reverse().map((c, i) => (
            <div key={`${c.name}-${c.at}`} className="flex items-center justify-between text-xs">
              <span className="text-slate-300 font-mono">{c.name}</span>
              <span className="text-slate-600">{new Date(c.at).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Autopilot() {
  const { channels, selectedChannelId } = useStudio();
  const [runs, setRuns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const q = selectedChannelId ? `?channel_id=${selectedChannelId}` : "";
      const { data } = await api.get(`/autopilot/runs${q}`);
      setRuns(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) { logger.error("autopilot runs load failed", e); } finally { setLoading(false); }
  }, [selectedChannelId, selectedId]);

  useEffect(() => { setSelectedId(null); }, [selectedChannelId]);
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);

  const channelName = channels.find((c) => c.id === selectedChannelId)?.data?.name || "All channels";

  return (
    <div className="animate-in-up">
      <div className="mb-6">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1 flex items-center gap-1.5"><Rocket className="w-3.5 h-3.5" /> Production</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Autopilot</h1>
        <p className="text-sm text-slate-400 mt-2">Autonomous topic-to-video. Never publishes silently — every run halts at review. Scope: <span className="text-slate-200">{channelName}</span></p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <CreateForm channelId={selectedChannelId} onCreated={(id) => { setSelectedId(id); load(); }} />
          {loading ? <LoadingState label="Loading runs…" /> : <RunsList runs={runs} selectedId={selectedId} onSelect={setSelectedId} />}
        </div>
        <div className="lg:col-span-3">
          {selectedId ? <Monitor runId={selectedId} /> : (
            <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-12">
              <EmptyState icon={Wand2} title="No run selected" description="Launch an autopilot run or pick one from the list to watch it live." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
